import { z } from 'zod'
import { LatLngSchema } from './locations/latLng'
import { TeamIdSchema, UserIdSchema, type EntityId, type TeamId } from './ids'
import { WithCustomFieldsSchema } from './customFieldMap'
import { AuditPropsSchema, UPLOAD_URL_DESCRIPTION } from './common'
import { ArchivableSchema } from './archive'

export type SingleOrOptionalEntityIds = EntityId | (EntityId | undefined)[]

export type WithAvatarUrl = {
  avatarUrl: string
}

export const TinyUserSchema = z
  .object({
    userId: UserIdSchema.meta({ description: 'Unique user identifier', example: 'U:user123' }),
    clientUserId: z
      .string()
      .optional()
      .meta({ id: 'userClientUserId', description: 'External client system user ID', example: 'EMP001' }),
    firstName: z.string().meta({ id: 'userFirstName', description: "User's first name", example: 'John' }),
    lastName: z.string().meta({ id: 'userLastName', description: "User's last name", example: 'Smith' }),
    preferredName: z
      .string()
      .optional()
      .meta({ id: 'userPreferredName', description: "User's preferred name", example: 'Johnny' }),
    avatarBlurhash: z.string().optional().meta({ id: 'userAvatarBlurhash', description: "BlurHash for user's avatar" }),
    avatarUrl: z.string().optional().meta({ id: 'userAvatarUrl', description: "URL to user's avatar image" }),
    lastAccess: z
      .number()
      .optional()
      .meta({ id: 'userLastAccess', description: 'Timestamp of last access', example: 1672531200000 }),
    isDeleted: z.boolean().optional().meta({ id: 'userIsDeleted', description: 'Whether the user is deleted' }),
    color: z.string().optional().meta({ id: 'tinyUserColor', description: 'Preferred color for area assignment' }),
  })
  .meta({
    id: 'tinyUser',
    title: 'Tiny User',
    description: 'Minimal user information for display purposes',
  })
export type TinyUser = z.infer<typeof TinyUserSchema>

export const TeamLevelSchema = z
  .number()
  .int()
  .min(1)
  .meta({ id: 'teamLevel', description: 'Level of the team in the hierarchy', example: 3 })
export type TeamLevel = z.infer<typeof TeamLevelSchema>

export const MeetingTimeSchema = z.object({
  day: z.number().min(1).max(7).meta({ id: 'meetingTimeDay', description: 'Day of the week (1-7)', example: 1 }),
  start: z
    .number()
    .min(0)
    .max(1439)
    .meta({ id: 'meetingTimeStart', description: 'Meeting start time in minutes since start of day', example: 600 }),
  end: z
    .number()
    .min(0)
    .max(1439)
    .meta({ id: 'meetingTimeEnd', description: 'Meeting end time in minutes since start of day', example: 660 }),
})
export type MeetingTime = z.infer<typeof MeetingTimeSchema>

export const UnsavedTeamSchema = z.object({
  ...WithCustomFieldsSchema.shape,
  teamId: TeamIdSchema.optional().meta({
    description: 'Unique team identifier, generated by the system if not provided',
    example: 'Team:team123',
  }),
  name: z.string().meta({ id: 'teamName', description: 'Friendly name of the team', example: 'Team MAP' }),
  parentId: TeamIdSchema.optional().meta({
    id: 'parentTeamId',
    description: 'ID of the parent team',
    example: 'Team:parent',
  }),
  level: TeamLevelSchema,
  externalId: z
    .string()
    .optional()
    .meta({ id: 'teamExternalId', description: 'External ID of the team', example: '12345' }),
  description: z
    .string()
    .optional()
    .meta({ id: 'teamDescription', description: 'Description of the team', example: 'This is a team.' }),
})
export type UnsavedTeam = z.infer<typeof UnsavedTeamSchema>

export const TeamDataSchema = z.object({
  ...ArchivableSchema.shape,
  ...AuditPropsSchema.shape,
  ...UnsavedTeamSchema.omit({ teamId: true }).shape,
  teamId: TeamIdSchema,
  timeZone: z.string().optional().meta({
    id: 'teamTimezone',
    description: 'Timezone the team operates in',
    example: 'America/Los_Angeles',
  }),
  officeLocation: LatLngSchema.optional().meta({
    id: 'teamOfficeLocation',
    description: 'Location of the team',
    example: { lat: 33.7, lng: -117.8 },
  }),
  officeAddress: z.string().optional().meta({
    id: 'teamOfficeAddress',
    description: 'Street address of the team',
    example: '1234 Main St, Irvine, CA 92780',
  }),
  meetingTimes: z
    .array(MeetingTimeSchema)
    .optional()
    .meta({
      id: 'teamMeetingTimes',
      description: 'Legacy scheduled meeting times for the team; new meeting configuration clients use /meeting/list',
      example: [{ day: 1, start: 600, end: 660 }],
    }),
  isCloserBoundary: z
    .boolean()
    .optional()
    .meta({ id: 'teamIsCloserBoundary', description: 'If true, closer search will not propagate to parent teams.' }),
  restrictVisibility: z
    .boolean()
    .optional()
    .meta({ id: 'teamRestrictVisibility', description: 'If true, team is not visible to other teams.' }),
  avatarBlurhash: z
    .string()
    .optional()
    .meta({ id: 'teamAvatarBlurhash', description: 'Blurhash representation of the team avatar.' }),
  avatarUrl: z.string().optional().meta({ id: 'teamAvatarUrl', description: 'URL of the team avatar.' }),
  parentTeamIds: z.array(TeamIdSchema).meta({ id: 'teamParentIds', description: "Team IDs in the team's hierarchy." }),
  uploadAvatarUrl: z.string().optional().meta({ id: 'teamUploadAvatarUrl', description: UPLOAD_URL_DESCRIPTION }),
})
export type TeamData = z.infer<typeof TeamDataSchema>

export const TinyTeamSchema = TeamDataSchema.pick({
  teamId: true,
  name: true,
  parentId: true,
  isDeleted: true,
  externalId: true,
  avatarBlurhash: true,
  avatarUrl: true,
  restrictVisibility: true,
  level: true,
  meetingTimes: true,
})
export type TinyTeam = z.infer<typeof TinyTeamSchema>

export const TeamLevelKeySchema = z.templateLiteral([z.literal('team'), TeamLevelSchema])
export type TeamLevelKey = z.infer<typeof TeamLevelKeySchema>
export const ManagerLevelKeySchema = z.templateLiteral([z.literal('manager'), TeamLevelSchema])
export type ManagerLevelKey = z.infer<typeof ManagerLevelKeySchema>
export type UserTeamsByLevel = Partial<Record<TeamLevelKey, TinyTeam[] | undefined>>
export const UserTeamsByLevelSchema = z.custom<UserTeamsByLevel>().meta({
  id: 'userTeamsByLevel',
  description: 'Dynamic team level keys. Keys are team{level}, such as team1, team2, or team12.',
  type: 'object',
  additionalProperties: TinyTeamSchema.array(),
  example: {
    team1: [{ teamId: 'Team:member', name: 'Member Team', level: 1 }],
    team12: [{ teamId: 'Team:regional', name: 'Regional Team', level: 12 }],
  },
})

export function toTeamId(team: { teamId: TeamId }): TeamId {
  return team.teamId
}
/** TODO: move to TinyTeam */

export type TinyTeamWithSingleManager = TinyTeam & {
  /** @deprecated see UserTeamData */
  manager?: TinyUser
}

export type TinyTeamWithLocked = TinyTeam & {
  locked?: boolean
}
